import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { tenantApi } from "@/lib/api/client";
import { contactApi } from "@/lib/api/contact";
import { Button, Text, Title, Box, Stack, Group, Flex, Loader, Badge } from "@mantine/core";
import { MessageSquare, Plus, Archive, ChevronRight, Headset, CheckCheck } from "lucide-react";

export default function SupportPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: conversations = [], isLoading } = useQuery({
    queryKey: ["support-conversations"],
    queryFn: () => tenantApi.listSupportConversations().then((r) => r.data),
  });

  const { data: groups = [] } = useQuery({
    queryKey: ["contact-groups"],
    queryFn: () => contactApi.getContactGroups().then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });

  // No member DELETE endpoint — "remove from my list" archives (the list is
  // fetched with include_archived=false, so an archived conversation drops off).
  const archiveMutation = useMutation({
    mutationFn: (id: number) => tenantApi.patchSupportConversation(id, "archived"),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["support-conversations"] }),
  });

  // Unread across every thread — drives both the "Mark all read" affordance
  // here and the Support nav badge (same query key, so they stay in step).
  const unreadTotal = conversations.reduce((sum, c) => sum + (c.unread_count ?? 0), 0);

  const markAllReadMutation = useMutation({
    mutationFn: () => tenantApi.markAllSupportConversationsRead(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["support-conversations"] }),
  });

  function formatDate(iso: string) {
    const d = new Date(iso);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    if (diff < 86_400_000) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
    if (diff < 7 * 86_400_000) return d.toLocaleDateString([], { weekday: "short" });
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  }

  return (
    <Box maw={672} mx="auto" px="md" py="lg">
      <Group justify="space-between" mb="md">
        <Title order={3}>Support</Title>
        <Group gap="xs">
          {unreadTotal > 0 && (
            <Button
              size="sm"
              variant="subtle"
              leftSection={<CheckCheck style={{ height: 16, width: 16 }} />}
              onClick={() => markAllReadMutation.mutate()}
              disabled={markAllReadMutation.isPending}
            >
              Mark all read
            </Button>
          )}
          <Button size="sm" leftSection={<Plus style={{ height: 16, width: 16 }} />} onClick={() => navigate("/support/new")}>
            New chat
          </Button>
        </Group>
      </Group>

      {groups.length > 0 && (
        <Box mb="lg">
          <Text fz="xs" fw={600} c="dimmed" tt="uppercase" mb="xs">
            Reach a person directly
          </Text>
          <Box style={{ borderRadius: 8, border: "1px solid var(--mantine-color-default-border)" }}>
            {groups.map((g, idx) => (
              <Group
                key={g.id}
                gap="sm"
                px="md"
                py="sm"
                wrap="nowrap"
                onClick={() => navigate(`/contact?group=${g.id}`)}
                style={{
                  cursor: "pointer",
                  borderTop: idx > 0 ? "1px solid var(--mantine-color-default-border)" : undefined,
                }}
              >
                <Headset style={{ height: 16, width: 16, color: "var(--mantine-color-dimmed)", flexShrink: 0 }} />
                <Box miw={0} flex={1}>
                  <Text fz="sm">{g.name}</Text>
                  {g.description && (
                    <Text fz="xs" c="dimmed" truncate="end">{g.description}</Text>
                  )}
                </Box>
                <ChevronRight style={{ height: 16, width: 16, color: "var(--mantine-color-dimmed)", flexShrink: 0 }} />
              </Group>
            ))}
          </Box>
        </Box>
      )}

      {isLoading && (
        <Flex justify="center" py="xl" c="dimmed">
          <Loader size={20} />
        </Flex>
      )}

      {!isLoading && conversations.length === 0 && (
        <Stack align="center" justify="center" py="xl" gap="sm" c="dimmed">
          <MessageSquare style={{ height: 40, width: 40, opacity: 0.3 }} />
          <Text fz="sm">No conversations yet.</Text>
          <Button leftSection={<Plus style={{ height: 16, width: 16 }} />} onClick={() => navigate("/support/new")}>
            Start a chat
          </Button>
        </Stack>
      )}

      {!isLoading && conversations.length > 0 && (
        <Box>
          <Text fz="xs" fw={600} c="dimmed" tt="uppercase" mb="xs">
            Conversations
          </Text>
          <Box style={{ borderRadius: 8, border: "1px solid var(--mantine-color-default-border)" }}>
          {conversations.map((conv, idx) => (
            <Group
              key={conv.id}
              gap="sm"
              px="md"
              py="sm"
              wrap="nowrap"
              onClick={() => navigate(`/support/${conv.id}`)}
              style={{
                cursor: "pointer",
                borderTop: idx > 0 ? "1px solid var(--mantine-color-default-border)" : undefined,
              }}
            >
              <MessageSquare style={{ height: 16, width: 16, color: "var(--mantine-color-dimmed)", flexShrink: 0 }} />
              <Box miw={0} flex={1}>
                <Group gap="xs" wrap="nowrap">
                  <Text fz="sm" truncate="end" fw={conv.unread_count > 0 ? 600 : 400}>{conv.preview}</Text>
                  {conv.unread_count > 0 && (
                    <Badge size="sm" color="red" circle style={{ flexShrink: 0 }} aria-label={`${conv.unread_count} unread`}>
                      {conv.unread_count}
                    </Badge>
                  )}
                </Group>
                <Text fz="xs" c="dimmed" mt={2}>
                  {formatDate(conv.created_at)} · {conv.message_count} messages
                </Text>
              </Box>
              <Group gap={4} style={{ flexShrink: 0 }}>
                <Button
                  variant="subtle"
                  size="compact-xs"
                  style={{ opacity: 0, transition: "opacity 150ms" }}
                  className="show-on-parent-hover"
                  onClick={(e) => { e.stopPropagation(); archiveMutation.mutate(conv.id); }}
                  disabled={archiveMutation.isPending}
                  aria-label="Archive"
                >
                  <Archive style={{ height: 14, width: 14 }} />
                </Button>
                <ChevronRight style={{ height: 16, width: 16, color: "var(--mantine-color-dimmed)" }} />
              </Group>
            </Group>
          ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
